import { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import type { Asset } from '@/types';
import { api } from '@/utils/api';
import {
  ROPE_WLL_CHART,
  ROPE_WLL_DISCLAIMER,
  WLL_UNKNOWN_MESSAGE,
  CUT_ROPE_HINT,
} from '@/components/rigging/equipmentPicker';

// §8.9: rope spec section shows for these types — both server enum names and
// the legacy form values already in use on the asset form.
// §8.9 rope-spec section applies to ROPE-RELEVANT gear only (Qwen §8.8 LOW
// fix: hoists/OTHER were showing rope diameter/construction spec fields too).
// 'ROPE'/'SLING' kept for legacy client-side type strings.
const ROPE_TYPES = ['RIGGING_SLING', 'ROPE', 'SLING'];

// S2-assets (Brian 2026-09-29 model): the type picker has ONE source —
// GET /api/assets/types (company custom types + built-in families). This
// static fallback keeps the form usable (and SSR-stable) before the fetch
// lands; the server list replaces it on mount.
interface TypeOption {
  id: number | null;
  name: string;
  family: string;
  defaultCadence: string;
  builtin: boolean;
}

const STATIC_BUILTINS: TypeOption[] = [
  { id: null, name: 'PPE_HARNESS', family: 'PPE_HARNESS', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'RIGGING_SLING', family: 'RIGGING_SLING', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'RIGGING_SHACKLE', family: 'RIGGING_SHACKLE', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'RIGGING_HOIST', family: 'RIGGING_HOIST', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'FALL_PROTECTION', family: 'FALL_PROTECTION', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'PPE_HELMET', family: 'PPE_HELMET', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'PPE_GLOVES', family: 'PPE_GLOVES', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'PPE_BOOTS', family: 'PPE_BOOTS', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'TEST_EQUIPMENT', family: 'TEST_EQUIPMENT', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'VEHICLE', family: 'VEHICLE', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'TOOL', family: 'TOOL', defaultCadence: 'NONE', builtin: true },
  { id: null, name: 'OTHER', family: 'OTHER', defaultCadence: 'NONE', builtin: true },
];

const TYPE_LABELS: Record<string, string> = {
  PPE_HARNESS: 'PPE Harness', PPE_HELMET: 'PPE Helmet', PPE_GLOVES: 'PPE Gloves',
  PPE_BOOTS: 'PPE Boots', FALL_PROTECTION: 'Fall Protection',
  RIGGING_SLING: 'Rigging Sling', RIGGING_SHACKLE: 'Rigging Shackle', RIGGING_HOIST: 'Rigging Hoist',
  TEST_EQUIPMENT: 'Test Equipment', VEHICLE: 'Vehicle', TOOL: 'Tool', OTHER: 'Other',
};

// Custom type select values are "custom:<id>"; built-ins stay bare family enum
// names (the form has always sent bare enum values).
const customChoice = (id: number) => `custom:${id}`;
const isCustomChoice = (v: string) => v.startsWith('custom:');
const customIdOf = (v: string) => parseInt(v.slice('custom:'.length), 10);

interface ContainerTarget {
  key: string; // 'VEHICLE:10' | 'TEAM:20' | 'MARKET:30' | 'PROJECT:40'
  label: string;
}

interface AssetFormModalProps {
  asset?: Asset | null;
  onClose: () => void;
  onSave: (assetData: any) => Promise<void>;
}

export function AssetFormModal({ asset, onClose, onSave }: AssetFormModalProps) {
  const [formData, setFormData] = useState(() => ({
    name: asset?.name || '',
    type: asset?.type || 'PPE_HARNESS',
    // S2: selected custom type id ('' = built-in family in `type`)
    customTypeId: (asset as any)?.customTypeId ? String((asset as any).customTypeId) : '',
    serialNumber: asset?.serialNumber || '',
    manufacturer: asset?.manufacturer || '',
    model: asset?.model || '',
    status: asset?.status || 'AVAILABLE',
    expirationDate: asset?.expirationDate ? asset.expirationDate.split('T')[0] : '',
    calibrationDue: asset?.calibrationDue ? asset.calibrationDue.split('T')[0] : '',
    purchasePrice: asset?.purchasePrice != null ? asset.purchasePrice.toString() : '',
    marketId: asset?.marketId ? asset.marketId.toString() : '',
    location: asset?.location || '',
    inspectionInterval: asset?.inspectionInterval || 'NONE',
    currentHolderId: asset?.currentHolderId ? asset.currentHolderId.toString() : '',
    // §8.8/§8.9 equipment-calc fields — all optional (zero-sticker first-class)
    containerType: asset?.containerType || '',
    containerLabel: asset?.containerLabel || '',
    // S2: real-object container pointer. Raw select value semantics
    // (S2-EXT-001): '' = untouched (key omitted on submit), '__clear__' =
    // explicit unlink (null sent), 'TYPE:id' = pick/replace.
    containerRefId: (asset as any)?.containerRefId
      ? `${(asset as any).containerRefType ?? 'VEHICLE'}:${(asset as any).containerRefId}`
      : '',
    wllLbs: asset?.wllLbs != null ? asset.wllLbs.toString() : '',
    ropeDiameter: asset?.ropeDiameter || '',
    ropeConstruction: asset?.ropeConstruction || '',
    ropeMaterial: asset?.ropeMaterial || '',
    // S2 (v1.4): vehicle weight + anchor capacity override for the
    // construction-plan anchor-utilization math. Stored nullable anywhere.
    weightLbs: (asset as any)?.weightLbs != null ? String((asset as any).weightLbs) : '',
    anchorCapacityLbs: (asset as any)?.anchorCapacityLbs != null ? String((asset as any).anchorCapacityLbs) : '',
  }));
  const [markets, setMarkets] = useState<Array<{ id: number; name: string; region: string | null }>>([]);
  const [types, setTypes] = useState<TypeOption[]>(STATIC_BUILTINS);
  const [containerTargets, setContainerTargets] = useState<ContainerTarget[]>([]);
  // DE-001: person picker source — company members from the non-admin-gated
  // eligible-workers list (falls back to admin-only /auth/users for admins).
  const [teamMembers, setTeamMembers] = useState<Array<{ id: number; label: string; role?: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // DE-001: load the people list for the Assigned To picker. Try the
    // non-admin-gated list first; a 403 falls back to /auth/users (admins).
    const loadTeamMembers = async () => {
      try {
        const res = await api.get('/assignments/eligible-workers');
        const rows = res.data?.data ?? res.data ?? [];
        if (Array.isArray(rows) && rows.length > 0) {
          setTeamMembers(rows.map((u: any) => ({
            id: u.id,
            label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `User ${u.id}`,
            role: u.role,
          })));
          return;
        }
      } catch { /* fall through to the admin list */ }
      try {
        const res = await api.get('/auth/users');
        const rows = res.data?.data ?? res.data ?? [];
        if (Array.isArray(rows)) {
          setTeamMembers(rows.map((u: any) => ({
            id: u.id,
            label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `User ${u.id}`,
            role: u.role,
          })));
        }
      } catch (e) {
        console.error('Error loading team members for assignment picker:', e);
      }
    };
    loadTeamMembers();
  }, []);

  useEffect(() => {
    // Load markets on mount
    const loadMarkets = async () => {
      try {
        const response = await api.get('/markets');
        if (response.data.success) {
          setMarkets(response.data.data);
        }
      } catch (error) {
        console.error('Error loading markets:', error);
      }
    };
    loadMarkets();

    // S2: one source for the type picker — custom types + built-in families.
    const loadTypes = async () => {
      try {
        const response = await api.get('/assets/types');
        if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
          setTypes(response.data.data);
        }
      } catch (error) {
        console.error('Error loading asset types:', error);
        // keep the static fallback — the form stays usable
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    if (asset) {
      setFormData((prev) => ({
        ...prev,
        name: asset.name || '',
        type: asset.type || 'PPE_HARNESS',
        customTypeId: (asset as any).customTypeId ? String((asset as any).customTypeId) : '',
        serialNumber: asset.serialNumber || '',
        manufacturer: asset.manufacturer || '',
        model: asset.model || '',
        status: asset.status || 'AVAILABLE',
        expirationDate: asset.expirationDate ? asset.expirationDate.split('T')[0] : '',
        calibrationDue: asset.calibrationDue ? asset.calibrationDue.split('T')[0] : '',
        purchasePrice: asset.purchasePrice ? asset.purchasePrice.toString() : '',
        marketId: asset.marketId ? asset.marketId.toString() : '',
        location: asset.location || '',
        inspectionInterval: asset.inspectionInterval || 'NONE',
        currentHolderId: asset.currentHolderId ? asset.currentHolderId.toString() : '',
        containerType: asset.containerType || '',
        containerLabel: asset.containerLabel || '',
        containerRefId: (asset as any).containerRefId
          ? `${(asset as any).containerRefType ?? 'VEHICLE'}:${(asset as any).containerRefId}`
          : '',
        wllLbs: asset.wllLbs != null ? asset.wllLbs.toString() : '',
        ropeDiameter: asset.ropeDiameter || '',
        ropeConstruction: asset.ropeConstruction || '',
        ropeMaterial: asset.ropeMaterial || '',
        weightLbs: (asset as any).weightLbs != null ? String((asset as any).weightLbs) : '',
        anchorCapacityLbs: (asset as any).anchorCapacityLbs != null ? String((asset as any).anchorCapacityLbs) : '',
      }));
    }
  }, [asset]);

  // S2: load the real objects a container pointer may reference. The server
  // validates the pointer anyway (this list is a picker, not a trust source).
  useEffect(() => {
    let cancelled = false;
    const loadTargets = async () => {
      if (!formData.containerType) {
        setContainerTargets([]);
        return;
      }
      try {
        const t = formData.containerType;
        if (t === 'TRUCK' || t === 'TRAILER') {
          const res = await api.get('/assets', { params: { type: 'VEHICLE' } });
          if (!cancelled) setContainerTargets((res.data.data ?? []).map((a: any) => ({ key: `VEHICLE:${a.id}`, label: a.name })));
        } else if (t === 'CREW') {
          const res = await api.get('/projects/crews');
          if (!cancelled) setContainerTargets((res.data.data ?? []).map((c: any) => ({ key: `TEAM:${c.id}`, label: c.name })));
        } else if (t === 'YARD') {
          const res = await api.get('/markets');
          if (!cancelled) setContainerTargets((res.data.data ?? []).map((m: any) => ({ key: `MARKET:${m.id}`, label: m.region ? `${m.name} (${m.region})` : m.name })));
        } else if (t === 'OTHER') {
          // S2-EXT-002: keys carry the target kind — market and project ids
          // are independent sequences and can collide numerically.
          const [mRes, pRes] = await Promise.all([api.get('/markets'), api.get('/projects')]);
          if (!cancelled) {
            setContainerTargets([
              ...(mRes.data.data ?? []).map((m: any) => ({ key: `MARKET:${m.id}`, label: `Yard: ${m.name}` })),
              ...(pRes.data.data ?? []).map((p: any) => ({ key: `PROJECT:${p.id}`, label: `Site: ${p.name}` })),
            ]);
          }
        }
      } catch (error) {
        console.error('Error loading container options:', error);
        if (!cancelled) setContainerTargets([]);
      }
    };
    loadTargets();
    return () => { cancelled = true; };
  }, [formData.containerType]);

  // Check if current type (family) requires calibration
  const requiresCalibration = formData.type === 'TEST_EQUIPMENT';
  const isVehicle = formData.type === 'VEHICLE';

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (isCustomChoice(v)) {
      const opt = types.find((t) => !t.builtin && t.id === customIdOf(v));
      setFormData((prev) => ({
        ...prev,
        customTypeId: String(customIdOf(v)),
        type: opt?.family || prev.type,
        // A custom type carries its default inspection cadence — prefill so
        // the picker reflects the type's default (user can still change it).
        inspectionInterval: opt?.defaultCadence && opt.defaultCadence !== 'NONE' ? opt.defaultCadence : prev.inspectionInterval,
      }));
    } else {
      setFormData((prev) => ({ ...prev, customTypeId: '', type: v }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const submitData: any = {
        ...formData,
        type: formData.type,
        customTypeId: formData.customTypeId ? parseInt(formData.customTypeId, 10) : null,
        expirationDate: formData.expirationDate || null,
        calibrationDue: formData.calibrationDue || null,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
        marketId: formData.marketId ? parseInt(formData.marketId) : null,
        currentHolderId: formData.currentHolderId ? parseInt(formData.currentHolderId) : null,
        // §8.8/§8.9: numeric/typed conversions; empty strings → null (no invented values)
        wllLbs: formData.wllLbs ? parseFloat(formData.wllLbs) : null,
        containerType: formData.containerType || null,
        weightLbs: formData.weightLbs ? parseFloat(formData.weightLbs) : null,
        anchorCapacityLbs: formData.anchorCapacityLbs ? parseFloat(formData.anchorCapacityLbs) : null,
      };

      // S2-EXT-001 (external audit 2026-09-30): pointer submit semantics —
      // untouched = key omitted (pointer survives), '__clear__' = explicit
      // unlink (null clears pointer + derived label), 'TYPE:id' = pick.
      const rawRef = formData.containerRefId;
      if (rawRef === '__clear__') {
        submitData.containerRefId = null;
      } else if (rawRef) {
        const [kind, id] = rawRef.split(':');
        submitData.containerRefId = parseInt(id, 10);
        if (kind === 'MARKET' || kind === 'PROJECT') submitData.containerRefTarget = kind;
      } else {
        delete submitData.containerRefId;
      }

      // Remove empty strings
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });

      await onSave(submitData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save asset');
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'containerType') {
        // Switching container kinds invalidates a previously picked pointer.
        next.containerRefId = '';
      }
      if (name === 'containerRefId' && value) {
        // Audit F2 (GLM 2026-09-30): picking a NEW linked record (or clearing
        // the link) clears any pre-filled label so the server derives/keeps
        // the right one — the label input stays a deliberate override only.
        next.containerLabel = '';
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {asset ? 'Edit Asset' : 'Add New Asset'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}
          <div className="space-y-4">
            {/* Asset Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Safety Harness #1"
              />
            </div>

            {/* Type & Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.customTypeId ? customChoice(parseInt(formData.customTypeId, 10)) : formData.type}
                  onChange={handleTypeChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {/* S2: company custom types first (one source: /api/assets/types) */}
                  {types.filter((t) => !t.builtin).length > 0 && (
                    <optgroup label="Company Types">
                      {types.filter((t) => !t.builtin).map((t) => (
                        <option key={`c-${t.id}`} value={customChoice(t.id!)}>
                          {t.name}{t.family ? ` — ${TYPE_LABELS[t.family] || t.family}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* Audit F4 (GLM 2026-09-30): an asset whose custom type was
                      deactivated keeps its link — show it honestly instead of
                      silently displaying the first built-in. */}
                  {formData.customTypeId &&
                    !types.some((t) => !t.builtin && String(t.id) === formData.customTypeId) && (
                      <option value={customChoice(parseInt(formData.customTypeId, 10))}>
                        (deactivated company type) — restore it from Manage Types
                      </option>
                    )}
                  <optgroup label="Built-in Types">
                    {/* Server AssetType enum values (§8.8 rigging types) */}
                    {types.filter((t) => t.builtin).map((t) => (
                      <option key={`b-${t.name}`} value={t.name}>
                        {TYPE_LABELS[t.name] || t.name}
                      </option>
                    ))}
                  </optgroup>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Missing a type? Admins can add company types from “Manage Types” on the Assets page.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="AVAILABLE">Available</option>
                  <option value="IN_USE">In Use</option>
                  <option value="MAINTENANCE">Maintenance</option>
                  <option value="DAMAGED">Damaged</option>
                  <option value="EXPIRED">Expired</option>
                </select>
              </div>
            </div>

            {/* Serial Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serial Number
              </label>
              <input
                type="text"
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., SN123456"
              />
            </div>

            {/* Manufacturer & Model */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Miller"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., DuraFlex"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date
                </label>
                <input
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {requiresCalibration && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Calibration Due
                  </label>
                  <input
                    type="date"
                    name="calibrationDue"
                    value={formData.calibrationDue}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Inspection Interval */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspection Interval
              </label>
              <select
                name="inspectionInterval"
                value={formData.inspectionInterval}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="NONE">No Regular Inspection</option>
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly (Every 3 months)</option>
                <option value="SEMI_ANNUAL">Semi-Annual (Every 6 months)</option>
                <option value="ANNUAL">Annual (Yearly)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                How often this asset needs to be inspected (a company type can pre-fill its default)
              </p>
            </div>

            {/* Office & Location */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Office / Market
                </label>
                <select
                  name="marketId"
                  value={formData.marketId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Office...</option>
                  {markets.map((market) => (
                    <option key={market.id} value={market.id}>
                      {market.name}{market.region ? ` (${market.region})` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Which office/market this asset belongs to
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sub-Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Warehouse rack 3"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional shelf label only — it drives no logic. Real structure comes from the container below.
                </p>
              </div>
            </div>

            {/* S2: structured container — real-object pointer + label */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Equipment Container (§8.8)</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Container Kind
                  </label>
                  <select
                    name="containerType"
                    value={formData.containerType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">None (unassigned)</option>
                    <option value="TRUCK">Truck</option>
                    <option value="TRAILER">Trailer</option>
                    <option value="CREW">Crew</option>
                    <option value="YARD">Yard</option>
                    <option value="OTHER">Other</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Plans scoped to this truck/trailer/crew will see this gear in the lift picker
                  </p>
                </div>
                {formData.containerType && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.containerType === 'TRUCK' ? 'Truck (vehicle asset)'
                        : formData.containerType === 'TRAILER' ? 'Trailer (vehicle asset)'
                        : formData.containerType === 'CREW' ? 'Crew'
                        : formData.containerType === 'YARD' ? 'Yard (market)'
                        : 'Site / Location'}
                    </label>
                    <select
                      name="containerRefId"
                      value={formData.containerRefId}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Not linked (keep current link if any)</option>
                      {/* S2-EXT-001: an existing pointer must be VISIBLE and
                          clearable — show it even before/while candidates load. */}
                      {formData.containerRefId &&
                        formData.containerRefId !== '__clear__' &&
                        !containerTargets.some((t) => t.key === formData.containerRefId) && (
                          <option value={formData.containerRefId}>
                            (current link{formData.containerLabel ? ` — ${formData.containerLabel}` : ''})
                          </option>
                        )}
                      {containerTargets.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                      {(asset as any)?.containerRefId || formData.containerRefId ? (
                        <option value="__clear__">— Clear link —</option>
                      ) : null}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {containerTargets.length === 0
                        ? 'No candidates loaded yet — if this persists, create the truck/crew/yard first. You can still type a label below.'
                        : 'Linking stores this record’s name as the container label (editable below).'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Container Label {formData.containerRefId ? '(optional override)' : ''}
                  </label>
                  <input
                    type="text"
                    name="containerLabel"
                    value={formData.containerLabel}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={formData.containerRefId ? 'Auto-filled from the linked record' : 'e.g., Truck 1435'}
                  />
                  {formData.containerRefId && !formData.containerLabel && (
                    <p className="mt-1 text-xs text-gray-500">
                      Leave blank to use the linked record’s name automatically.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* §8.8: WLL — the calculator reads this automatically; empty = flagged, never guessed */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WLL (lbs)
                </label>
                <input
                  type="number"
                  name="wllLbs"
                  value={formData.wllLbs}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty if WLL unknown"
                />
                {formData.wllLbs === '' && (
                  <p className="mt-1 text-xs text-amber-700">{WLL_UNKNOWN_MESSAGE}</p>
                )}
              </div>
            </div>

            {/* S2 (v1.4): vehicle weight + anchor capacity — construction-plan
                anchor math (truck is the plan anchor; utilization % display) */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isVehicle ? 'Vehicle Weight (lbs)' : 'Weight (lbs)'}
                </label>
                <input
                  type="number"
                  name="weightLbs"
                  value={formData.weightLbs}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty if unknown"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Anchor Capacity (lbs)
                </label>
                <input
                  type="number"
                  name="anchorCapacityLbs"
                  value={formData.anchorCapacityLbs}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional explicit override"
                />
              </div>
              <p className="md:col-span-2 -mt-2 text-xs text-gray-500">
                {isVehicle
                  ? 'Vehicles anchor construction-plan lifts: the plan checks the hoisted weight against the truck’s weight and shows % utilization (amber ≥80%, red >100%). Anchor capacity is optional — leave blank to use weight-based defaults.'
                  : 'Optional anywhere — vehicles use these for the construction-plan anchor math (utilization %). Both are stored even when unknown.'}
              </p>
            </div>

            {/* §8.9 rope spec + proposed-default WLL chart */}
            {ROPE_TYPES.includes(formData.type) && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Rope Spec (§8.9)</h3>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                  {CUT_ROPE_HINT}
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diameter</label>
                    <input
                      type="text"
                      name="ropeDiameter"
                      value={formData.ropeDiameter}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder='e.g., 3/8 in'
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Construction</label>
                    <select
                      name="ropeConstruction"
                      value={formData.ropeConstruction}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Unknown</option>
                      <option value="braided">Braided</option>
                      <option value="3-strand">3-strand</option>
                      <option value="wire">Wire</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                    <input
                      type="text"
                      name="ropeMaterial"
                      value={formData.ropeMaterial}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., polyester, steel core"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Common rope WLL reference</p>
                  <table className="text-xs border border-gray-200 rounded w-full" data-testid="rope-wll-chart">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left px-2 py-1">Diameter</th>
                        <th className="text-left px-2 py-1">Construction</th>
                        <th className="text-left px-2 py-1">Proposed WLL (lb)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ROPE_WLL_CHART.map((row, i) => (
                        <tr key={`${row.diameterIn}-${row.construction}-${i}`} className="border-t border-gray-100">
                          <td className="px-2 py-1">{row.diameterIn} in</td>
                          <td className="px-2 py-1">{row.construction}</td>
                          <td className="px-2 py-1">{row.proposedWllLbs.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-1 text-xs text-gray-500" data-testid="rope-wll-disclaimer">
                    {ROPE_WLL_DISCLAIMER}
                  </p>
                </div>
              </div>
            )}

            {/* Assigned To — DE-001 (user-POV 2026-10-06): the old raw
                "Enter user ID" number input forced re-keying an internal id.
                A person picker sourced from the non-admin-gated
                /assignments/eligible-workers list replaces it; the value is
                still the user id, so the server contract is unchanged. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assigned To
              </label>
              {asset?.currentHolder && (
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium">
                    Currently: {asset.currentHolder.firstName} {asset.currentHolder.lastName}
                  </p>
                </div>
              )}
              <select
                name="currentHolderId"
                value={formData.currentHolderId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Unassigned — no person holds this asset</option>
                {/* Keep a picked holder visible even if the picker list is
                    still loading or the holder is outside the fetched list. */}
                {formData.currentHolderId && !teamMembers.some((m) => String(m.id) === formData.currentHolderId) && (
                  <option value={formData.currentHolderId}>
                    (current holder, id {formData.currentHolderId})
                  </option>
                )}
                {teamMembers.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.label}{m.role ? ` — ${m.role}` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Pick the person who holds this gear, or keep “Unassigned” to clear the assignment
              </p>
            </div>

            {/* Purchase Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Price
              </label>
              <input
                type="number"
                name="purchasePrice"
                value={formData.purchasePrice}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {asset ? 'Save Changes' : 'Add Asset'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
